import Vue from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';
import i18n from './plugins/i18n';
import { init } from '@module-federation/runtime';
import { EventBus } from './plugins/eventBus';
import { createStoreAdapter } from './store/store-adapter';


// New Relic Browser Agent setup
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import performanceMonitor from './utility/performance-monitor';

// Start platform load time tracking
const endPlatformLoad = performanceMonitor.trackPlatformLoad();

// New Relic configuration - Comprehensive metrics collection
const options = {
  init: {
    // New Relic license key - hardcoded for POC
    licenseKey: 'cb47998090cd38618598be8c00c55adaFFFFNRAL',
    
    // Application name - hardcoded for POC
    applicationID: '1589145812',
    
    // Enable distributed tracing
    distributedTracing: {
      enabled: true
    },
    
    // Custom attributes for better filtering and analysis
    customAttributes: {
      environment: process.env.NODE_ENV || 'production',
      deploymentUrl: process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : 'host-app-vue2.vercel.app',
      appType: 'micro-frontend-host',
      framework: 'vue2',
      version: '1.0.0',
      buildTime: new Date().toISOString(),
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`
    },
    
    // Enable comprehensive performance monitoring
    performance: {
      enabled: true,
      // Enable all performance features
      longTask: true,
      memory: true,
      navigationTiming: true,
      resourceTiming: true
    },
    
    // Enable comprehensive error tracking
    error: {
      enabled: true,
      // Capture all error types
      captureUnhandledRejections: true,
      captureUncaughtExceptions: true
    },
    
    // Enable comprehensive session tracking
    session: {
      enabled: true,
      // Track session duration and interactions
      sessionDuration: true,
      sessionTrace: true
    },
    
    // Enable comprehensive page view tracking
    pageView: {
      enabled: true,
      // Track all page views and SPA navigation
      spa: true,
      timing: true
    },
    
    // Enable comprehensive AJAX monitoring
    ajax: {
      enabled: true,
      // Monitor all network requests
      captureRequestHeaders: true,
      captureResponseHeaders: true,
      captureRequestBody: true,
      captureResponseBody: true
    },
    
    // Enable Core Web Vitals monitoring
    coreWebVitals: {
      enabled: true,
      // Track all Core Web Vitals
      lcp: true,
      fid: true,
      cls: true,
      fcp: true,
      ttfb: true
    },
    
    // Enable SPA monitoring
    spa: {
      enabled: true,
      // Track SPA navigation and component loading
      trackRouteChanges: true,
      trackComponentLoads: true
    },
    
    // Enable JavaScript error monitoring
    jsErrors: {
      enabled: true,
      // Capture all JavaScript errors
      captureUnhandledRejections: true,
      captureUncaughtExceptions: true
    },
    
    // Enable resource timing
    resourceTiming: {
      enabled: true,
      // Track all resource loading times
      captureAllResources: true
    },
    
    // Enable custom metrics
    customMetrics: {
      enabled: true,
      // Allow custom metric collection
      allowCustomMetrics: true
    },

    // Enable BrowserPerformance events (marks and measures) - NEW FEATURE
    browserPerformance: {
      enabled: true,
      captureMarks: true,
      captureMeasures: true
    }
  },
  
  // Loader configuration - hardcoded for POC
  loader_config: {
    accountID: '7225477',
    agentID: '1589145812',
    licenseKey: 'cb47998090cd38618598be8c00c55adaFFFFNRAL',
    applicationID: '1589145812'
  }
};

// Initialize New Relic Browser Agent
const nrba = new BrowserAgent(options);
nrba.start();

// Connect performance monitor with New Relic agent
performanceMonitor.setNewRelicAgent(nrba);

// Start comprehensive monitoring
performanceMonitor.startComprehensiveMonitoring();

// Enable Session Replay (configured after agent initialization)
if (window.newrelic) {
  window.newrelic.setCustomAttribute('sessionReplayEnabled', true);
  console.log('Session Replay enabled for POC');
}

console.log('New Relic Browser Agent initialized successfully');

console.log('USER_APP_URL:', process.env.USER_APP_URL);


Vue.prototype.$eventBus = EventBus;

// This step configures the environment to understand and load remote modules.
init({
  name: 'hostApp',
  remotes: [
    {
      name: 'userAppVue3',
      entry: process.env.NODE_ENV === 'development' 
        ? 'http://localhost:8081/remoteEntry.js'
        : `${process.env.USER_APP_URL}/remoteEntry.js`,
    },
    {
      name: 'editUserAppVue3',
      entry: process.env.NODE_ENV === 'development' 
        ? 'http://localhost:8082/remoteEntry.js'
        : `${process.env.EDIT_USER_APP_URL}/remoteEntry.js`,
    },
  ],
});

Vue.config.productionTip = false;



// Create the store adapter instance.
// This instance will bridge the Vuex store to Vue 3 remote applications,
// making it reactive for them and providing action dispatchers.
const storeAdapter = createStoreAdapter(store, EventBus);

new Vue({
  router,
  store,
  i18n,
  render: h => h(App),
  // Provide the store adapter, EventBus, router, and i18n to all child components,
  provide() {
    return {
      storeAdapter: storeAdapter,
      eventBus: EventBus,
      i18n: {
        t: i18n.t.bind(i18n),
        locale: i18n.locale,
      },
      hostRouter: {
        push: router.push.bind(router),
        replace: router.replace.bind(router),
        currentRoute: router.currentRoute
      },
    };
  },
  mounted() {
    // End platform load time tracking
    endPlatformLoad();
    
    // Track initial page load
    const endPageLoad = performanceMonitor.trackComponentLoad('App');
    setTimeout(() => {
      endPageLoad();
    }, 100);
    
    // Track store actions
    const endFetchUsers = performanceMonitor.trackStoreAction('fetchUsers');
    this.$store.dispatch('fetchUsers').finally(() => {
      endFetchUsers();
    });

    EventBus.$on('storeUpdated', () => {
      console.log('Host App: Store updated event received!');
    });

    EventBus.$on('remote:updateUser', async ({ id, userData, callback }) => {
      console.log('Host App: Received remote:updateUser event', { id, userData });
      try {
        await this.$store.dispatch('updateUser', { id, userData });
        if (callback) callback({ success: true });
        console.log('Host App: User updated successfully after remote request.');
      } catch (error) {
        console.error('Host App: Error updating user from remote request:', error);
        if (callback) callback({ success: false, error: error.message });
      }
    });

    EventBus.$on('remote:addUser', async ({ userData, callback }) => {
      console.log('Host App: Received remote:addUser event', { userData });
      try {
        await this.$store.dispatch('addUser', userData);
        if (callback) callback({ success: true });
        console.log('Host App: User added successfully after remote request.');
      } catch (error) {
        console.error('Host App: Error adding user from remote request:', error);
        if (callback) callback({ success: false, error: error.message });
      }
    });

    EventBus.$on('remote:deleteUser', async ({ userId, callback }) => {
      console.log('Host App: Received remote:deleteUser event for ID:', userId);
      try {
        await this.$store.dispatch('deleteUser', userId);
        if (callback) callback({ success: true });
        console.log('Host App: User deleted successfully after remote request.');
      } catch (error) {
        console.error('Host App: Error deleting user from remote request:', error);
        if (callback) callback({ success: false, error: error.message });
      }
    });
  },
}).$mount('#app');
